import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Badge,
  Button,
  Typography,
  Space,
  Tabs,
  Switch,
  Divider,
  Avatar,
  Tag,
  Empty,
  Tooltip,
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;

const Notifications = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'warning',
      title: 'Low Stock Alert',
      message: 'Bluetooth Speaker (BS-002) is running low on stock (8 units remaining)',
      timestamp: '2024-01-20T10:30:00Z',
      read: false,
      category: 'inventory',
    },
    {
      id: 2,
      type: 'info',
      title: 'Purchase Order Received',
      message: 'PO-2024-001 from Tech Supplies Inc has been received',
      timestamp: '2024-01-20T09:15:00Z',
      read: false,
      category: 'purchasing',
    },
    {
      id: 3,
      type: 'success',
      title: 'Order Completed',
      message: 'Order ORD-2024-005 has been successfully fulfilled',
      timestamp: '2024-01-19T16:45:00Z',
      read: true,
      category: 'orders',
    },
    {
      id: 4,
      type: 'error',
      title: 'System Alert',
      message: 'Failed to sync inventory data. Please check connection.',
      timestamp: '2024-01-19T14:20:00Z',
      read: false,
      category: 'system',
    },
  ]);

  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    lowStockAlerts: true,
    orderUpdates: true,
    systemAlerts: true,
  });

  const [activeTab, setActiveTab] = useState('all');

  const getIcon = (type) => {
    switch (type) {
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'success':
        return <CheckOutlined style={{ color: '#52c41a' }} />;
      case 'info':
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const getTagColor = (category) => {
    switch (category) {
      case 'inventory':
        return 'orange';
      case 'purchasing':
        return 'blue';
      case 'orders':
        return 'green';
      case 'system':
        return 'red';
      default:
        return 'default';
    }
  };

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === 'unread') return !notification.read;
    if (activeTab === 'read') return notification.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const tabItems = [
    {
      key: 'all',
      label: (
        <span>
          All Notifications
          <Badge count={notifications.length} style={{ marginLeft: 8 }} />
        </span>
      ),
    },
    {
      key: 'unread',
      label: (
        <span>
          Unread
          <Badge count={unreadCount} style={{ marginLeft: 8 }} />
        </span>
      ),
    },
    {
      key: 'read',
      label: 'Read',
    },
    {
      key: 'settings',
      label: (
        <span>
          <SettingOutlined /> Settings
        </span>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Title level={2} className="mb-0">
            <BellOutlined className="mr-2" />
            Notifications
          </Title>
          <Text type="secondary">
            Stay updated with important alerts and system notifications
          </Text>
        </div>
        <Space>
          <Button onClick={markAllAsRead} disabled={unreadCount === 0}>
            Mark All as Read
          </Button>
        </Space>
      </div>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems.map(item => ({
            ...item,
            children: item.key === 'settings' ? (
              <div className="space-y-6">
                <Title level={4}>Notification Preferences</Title>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <Text strong>Email Notifications</Text>
                      <br />
                      <Text type="secondary">Receive notifications via email</Text>
                    </div>
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(checked) =>
                        setSettings(prev => ({ ...prev, emailNotifications: checked }))
                      }
                    />
                  </div>

                  <Divider />

                  <div className="flex justify-between items-center">
                    <div>
                      <Text strong>Push Notifications</Text>
                      <br />
                      <Text type="secondary">Receive browser push notifications</Text>
                    </div>
                    <Switch
                      checked={settings.pushNotifications}
                      onChange={(checked) =>
                        setSettings(prev => ({ ...prev, pushNotifications: checked }))
                      }
                    />
                  </div>

                  <Divider />

                  <div className="flex justify-between items-center">
                    <div>
                      <Text strong>Low Stock Alerts</Text>
                      <br />
                      <Text type="secondary">Get notified when products are running low</Text>
                    </div>
                    <Switch
                      checked={settings.lowStockAlerts}
                      onChange={(checked) =>
                        setSettings(prev => ({ ...prev, lowStockAlerts: checked }))
                      }
                    />
                  </div>

                  <Divider />

                  <div className="flex justify-between items-center">
                    <div>
                      <Text strong>Order Updates</Text>
                      <br />
                      <Text type="secondary">Notifications for order status changes</Text>
                    </div>
                    <Switch
                      checked={settings.orderUpdates}
                      onChange={(checked) =>
                        setSettings(prev => ({ ...prev, orderUpdates: checked }))
                      }
                    />
                  </div>

                  <Divider />

                  <div className="flex justify-between items-center">
                    <div>
                      <Text strong>System Alerts</Text>
                      <br />
                      <Text type="secondary">Important system and security notifications</Text>
                    </div>
                    <Switch
                      checked={settings.systemAlerts}
                      onChange={(checked) =>
                        setSettings(prev => ({ ...prev, systemAlerts: checked }))
                      }
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {filteredNotifications.length === 0 ? (
                  <Empty
                    description={
                      activeTab === 'unread'
                        ? 'No unread notifications'
                        : activeTab === 'read'
                        ? 'No read notifications'
                        : 'No notifications'
                    }
                  />
                ) : (
                  <List
                    itemLayout="horizontal"
                    dataSource={filteredNotifications}
                    renderItem={(notification) => (
                      <List.Item
                        className={`${!notification.read ? 'bg-blue-50' : ''} p-4 rounded-lg mb-2`}
                        actions={[
                          !notification.read && (
                            <Tooltip title="Mark as read">
                              <Button
                                type="text"
                                icon={<CheckOutlined />}
                                onClick={() => markAsRead(notification.id)}
                              />
                            </Tooltip>
                          ),
                          <Tooltip title="Delete">
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => deleteNotification(notification.id)}
                            />
                          </Tooltip>,
                        ].filter(Boolean)}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              icon={getIcon(notification.type)}
                              style={{
                                backgroundColor: notification.read ? '#f5f5f5' : '#e6f7ff',
                              }}
                            />
                          }
                          title={
                            <div className="flex items-center space-x-2">
                              <Text strong={!notification.read}>
                                {notification.title}
                              </Text>
                              <Tag color={getTagColor(notification.category)}>
                                {notification.category}
                              </Tag>
                              {!notification.read && (
                                <Badge status="processing" />
                              )}
                            </div>
                          }
                          description={
                            <div className="space-y-1">
                              <Text>{notification.message}</Text>
                              <div className="flex items-center text-gray-500">
                                <ClockCircleOutlined className="mr-1" />
                                <Text type="secondary" className="text-sm">
                                  {moment(notification.timestamp).fromNow()}
                                </Text>
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </div>
            ),
          }))}
        />
      </Card>
    </div>
  );
};

export default Notifications;
